import { useState, useEffect, useRef, useCallback } from 'react';
import { assetsService, type EstimateAsset } from '@/services/assets-service';
import { estimatesService } from '@/services/estimates-service';

interface AssetsPanelProps {
  estimateId: string;
}

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB original
const MAX_DIMENSION = 1920; // max width or height after resize
const JPEG_QUALITY = 0.8; // compression quality
const MAX_PAYLOAD_BYTES = 3.5 * 1024 * 1024; // keep base64 under Vercel 4.5MB limit

/**
 * Compress/resize an image file to a base64 JPEG data URL.
 * Keeps images under Vercel's serverless payload limit.
 */
function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      // Scale down if needed
      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0, width, height);

      // Try JPEG first, fall back to lower quality if too large
      let quality = JPEG_QUALITY;
      let dataUrl = canvas.toDataURL('image/jpeg', quality);
      while (dataUrl.length > MAX_PAYLOAD_BYTES && quality > 0.3) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/jpeg', quality);
      }
      resolve(dataUrl);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

export function AssetsPanel({ estimateId }: AssetsPanelProps) {
  const [assets, setAssets] = useState<EstimateAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Pin letter lookup: assetId → letter (A, B, C...)
  const [pinLetters, setPinLetters] = useState<Record<string, string>>({});

  // Helper: convert index to letter (0→A, 1→B, ..., 25→Z, 26→AA)
  function indexToLetter(i: number): string {
    let result = '';
    let n = i;
    do {
      result = String.fromCharCode(65 + (n % 26)) + result;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return result;
  }

  // Fetch pin letters directly from API (works even if MapPanel isn't mounted)
  useEffect(() => {
    let cancelled = false;
    estimatesService.getMapPins(estimateId).then(pins => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      pins.forEach((pin, idx) => {
        if (pin.photoAssetId) map[pin.photoAssetId] = indexToLetter(idx);
      });
      setPinLetters(map);
    }).catch(() => {}); // silent if endpoint unavailable
    return () => { cancelled = true; };
  }, [estimateId]);

  // Also listen for live updates from MapPanel (if both are mounted)
  useEffect(() => {
    function onPinLetters(e: Event) {
      const detail = (e as CustomEvent).detail as Record<string, string>;
      setPinLetters(detail || {});
    }
    window.addEventListener('paver-pin-letters', onPinLetters);
    return () => window.removeEventListener('paver-pin-letters', onPinLetters);
  }, []);

  // Sync thumb heights per row so side-by-side images match the shortest
  const syncRowHeights = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll<HTMLElement>('.assets-thumb'));
    // Reset heights first so we can measure natural sizes
    cards.forEach(c => { c.style.height = ''; });

    // Group cards by their top offset (same row)
    const rows = new Map<number, HTMLElement[]>();
    cards.forEach(card => {
      const top = Math.round(card.getBoundingClientRect().top);
      if (!rows.has(top)) rows.set(top, []);
      rows.get(top)!.push(card);
    });

    // For each row, set all thumbs to the minimum height
    rows.forEach(rowCards => {
      if (rowCards.length <= 1) return;
      const minH = Math.min(...rowCards.map(c => c.offsetHeight));
      rowCards.forEach(c => { c.style.height = minH + 'px'; });
    });
  }, []);

  // Re-sync on asset changes and window resize
  useEffect(() => {
    if (assets.length === 0) return;
    // Wait for images to load before measuring
    const grid = gridRef.current;
    if (!grid) return;
    const imgs = Array.from(grid.querySelectorAll<HTMLImageElement>('.assets-thumb img'));
    let loaded = 0;
    const total = imgs.length;
    function checkDone() {
      loaded++;
      if (loaded >= total) syncRowHeights();
    }
    imgs.forEach(img => {
      if (img.complete) { checkDone(); }
      else { img.addEventListener('load', checkDone, { once: true }); }
    });

    window.addEventListener('resize', syncRowHeights);
    return () => { window.removeEventListener('resize', syncRowHeights); };
  }, [assets, syncRowHeights]);

  // Load assets on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await assetsService.list(estimateId);
        if (!cancelled) setAssets(data);
      } catch {
        // silent – empty list
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [estimateId]);

  // Upload handler
  const uploadFiles = useCallback(async (files: File[]) => {
    const valid = files.filter(f => {
      if (!ACCEPTED_TYPES.includes(f.type)) return false;
      if (f.size > MAX_FILE_SIZE) return false;
      return true;
    });
    if (valid.length === 0) {
      setError('Please upload JPG, PNG, or WEBP files under 10 MB.');
      return;
    }

    setUploading(true);
    setError(null);
    let failCount = 0;
    for (const file of valid) {
      try {
        const base64 = await compressImage(file);
        const asset = await assetsService.upload(estimateId, {
          base64,
          fileName: file.name,
          category: 'map',
        });
        setAssets(prev => [...prev, asset]);
      } catch (err: any) {
        failCount++;
        const serverMsg = err?.response?.data?.message || err?.message || 'Unknown error';
        console.error('Asset upload failed:', serverMsg, err);
        setError(`Upload failed: ${serverMsg}`);
      }
    }
    setUploading(false);
  }, [estimateId]);

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  }, [uploadFiles]);

  // File input change
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadFiles]);

  // Update category
  const updateCategory = useCallback(async (assetId: string, category: string) => {
    setAssets(prev => prev.map(a => a.id === assetId ? { ...a, category } : a));
    try {
      await assetsService.update(estimateId, assetId, { category });
    } catch {
      // revert on error – reload
      const data = await assetsService.list(estimateId);
      setAssets(data);
    }
  }, [estimateId]);

  // Update description (debounced save on blur)
  const updateDescription = useCallback(async (assetId: string, description: string) => {
    const value = description.trim() || null;
    setAssets(prev => prev.map(a => a.id === assetId ? { ...a, description: value } : a));
    try {
      await assetsService.update(estimateId, assetId, { description: value });
    } catch {
      const data = await assetsService.list(estimateId);
      setAssets(data);
    }
  }, [estimateId]);

  // Delete
  const deleteAsset = useCallback(async (assetId: string) => {
    const asset = assets.find(a => a.id === assetId);
    const label = asset?.fileName ? `"${asset.fileName}"` : 'this asset';
    if (!window.confirm(`Delete ${label}? This will permanently remove it from the estimate and cannot be undone.`)) return;
    setAssets(prev => prev.filter(a => a.id !== assetId));
    try {
      await assetsService.remove(estimateId, assetId);
    } catch {
      // revert on error – reload
      const data = await assetsService.list(estimateId);
      setAssets(data);
    }
  }, [estimateId, assets]);

  if (loading) {
    return (
      <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--s400)', fontSize: 14 }}>
        Loading assets…
      </div>
    );
  }

  return (
    <div>
      {/* Error message */}
      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Upload Dropzone */}
      <div
        className={`assets-dropzone${dragOver ? ' assets-dropzone-active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <div className="assets-dropzone-icon">
          {uploading ? (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M12 2v4m0 12v4m-7.07-3.93l2.83-2.83m8.48-8.48l2.83-2.83M2 12h4m12 0h4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83" />
            </svg>
          ) : (
            <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 16V4m0 0L8 8m4-4l4 4"/><path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2"/></svg>
          )}
        </div>
        <div className="assets-dropzone-text">
          {uploading ? 'Uploading…' : <>Drag images here or <span>browse files</span></>}
        </div>
        <div className="assets-dropzone-formats">JPG, JPEG, PNG, WEBP (max 10 MB)</div>
      </div>

      {/* Thumbnail Grid */}
      {assets.length > 0 && (
        <div className="assets-grid" ref={gridRef}>
          {assets.map((asset) => (
            <div key={asset.id} className="assets-card">
              <div className="assets-thumb" style={{ position: 'relative' }}>
                {pinLetters[asset.id] && (
                  <span className="assets-pin-badge">{pinLetters[asset.id]}</span>
                )}
                <img src={asset.url} alt={asset.fileName} />
                <div className="assets-thumb-actions">
                  <button className="assets-thumb-btn" title="View" onClick={() => setLightboxUrl(asset.url)}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                  </button>
                  <button className="assets-thumb-btn" title="Delete" onClick={() => deleteAsset(asset.id)}>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                  </button>
                </div>
              </div>
              <select
                className="assets-card-select"
                value={asset.category}
                onChange={(e) => updateCategory(asset.id, e.target.value)}
              >
                <option value="map">Map</option>
                {/* Label only — the stored value stays "addendum" so existing
                    assets and the proposal pipeline keep working unmigrated. */}
                <option value="addendum">Attachments</option>
                <option value="asphalt">Asphalt</option>
                <option value="concrete">Concrete</option>
                <option value="sealcoat">Sealcoat</option>
                <option value="grading">Grading</option>
                <option value="striping">Striping</option>
                <option value="excluded">Not Included in Proposal</option>
              </select>
              <textarea
                className="assets-card-desc"
                placeholder="Add a description…"
                defaultValue={asset.description || ''}
                onBlur={(e) => updateDescription(asset.id, e.target.value)}
                rows={2}
              />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {assets.length === 0 && !uploading && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--s400)', fontSize: 13 }}>
          No assets uploaded yet
        </div>
      )}

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="assets-lightbox" onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Preview" />
          <button className="assets-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="24" height="24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}
